import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { format, formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { adminSocial, alerts as alertsApi } from "@/lib/api";
import { postLength } from "@/lib/socialText";
import {
  buildRows,
  CSV_TEMPLATE,
  isImageLink,
  MAX_BULK_ROWS,
  toBulkInput,
  type BulkRow,
  type UploadedImage,
} from "@/lib/socialCsv";
import { BLUESKY_MAX_IMAGE_BYTES, formatMb, isVideoType, MEDIA_ACCEPT, mediaProblem } from "@/lib/socialMedia";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Icon } from "@/components/ui/icon";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type {
  Alert,
  SocialAccount,
  SocialAutomationConfig,
  SocialConfig,
  SocialConnectTarget,
  SocialPlatform,
  SocialPost,
  SocialPostInput,
  SocialPostStatus,
} from "@/types";

const PLATFORM_LABEL: Record<SocialPlatform, string> = { linkedin: "LinkedIn", x: "X", bluesky: "Bluesky" };
const PLATFORM_ICON: Record<SocialPlatform, string> = { linkedin: "linkedin-01", x: "new-twitter", bluesky: "bluesky" };
const ALL_PLATFORMS: SocialPlatform[] = ["linkedin", "x", "bluesky"];
const AUTOMATION_PAGE_TYPES = [
  { value: "pricing", label: "Pricing" },
  { value: "features", label: "Features" },
  { value: "changelog", label: "Changelog" },
  { value: "blog", label: "Blog" },
  { value: "careers", label: "Careers" },
  { value: "docs", label: "Documentation" },
  { value: "homepage", label: "Homepage" },
  { value: "custom", label: "Custom pages" },
];

const STATUS_BADGE: Record<SocialPostStatus, { label: string; variant: "secondary" | "outline" | "warning" | "success" | "destructive" }> = {
  draft: { label: "Draft", variant: "secondary" },
  scheduled: { label: "Scheduled", variant: "outline" },
  publishing: { label: "Publishing", variant: "warning" },
  published: { label: "Published", variant: "success" },
  partial: { label: "Partly published", variant: "warning" },
  failed: { label: "Failed", variant: "destructive" },
};

type Filter = "upcoming" | "drafts" | "published" | "attention" | "all";

const FILTERS: { value: Filter; label: string; statuses: SocialPostStatus[] | null }[] = [
  { value: "upcoming", label: "Scheduled", statuses: ["scheduled", "publishing"] },
  { value: "drafts", label: "Drafts", statuses: ["draft"] },
  { value: "attention", label: "Needs attention", statuses: ["failed", "partial"] },
  { value: "published", label: "Published", statuses: ["published"] },
  { value: "all", label: "All", statuses: null },
];

const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

function formatWhen(iso: string | null): string {
  return iso ? format(new Date(iso), "EEE d MMM yyyy, HH:mm") : "";
}

function toLocalInputValue(date: Date): string {
  return format(date, "yyyy-MM-dd'T'HH:mm");
}

function defaultScheduleTime(): string {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(9, 0, 0, 0);
  return toLocalInputValue(date);
}

// ─── Small pieces ─────────────────────────────────────────────────────────────

function AccountAvatar({ account, size = 28 }: { account: Pick<SocialAccount, "avatarUrl" | "displayName" | "platform">; size?: number }) {
  return (
    <div
      className="relative shrink-0 rounded-full border border-border bg-muted overflow-hidden flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      {account.avatarUrl ? (
        <img src={account.avatarUrl} alt={account.displayName} className="h-full w-full object-cover" />
      ) : (
        <Icon name={PLATFORM_ICON[account.platform]} size={size / 2} />
      )}
    </div>
  );
}

/** Admin media needs auth, so load it through fetch rather than <img src>. */
function AuthedMedia({
  imageKey,
  contentType,
  alt,
  className,
  children,
}: {
  imageKey: string;
  contentType?: string | null;
  alt: string;
  className?: string;
  children?: React.ReactNode;
}) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    void adminSocial.imageObjectUrl(imageKey).then((result) => {
      if (cancelled) {
        if (result) URL.revokeObjectURL(result);
        return;
      }
      objectUrl = result;
      setUrl(result);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [imageKey]);

  if (!url) return <Skeleton className={cn("h-48 w-full rounded-lg", className)} />;
  return (
    <MediaPreview src={url} contentType={contentType} alt={alt} className={className}>
      {children}
    </MediaPreview>
  );
}

const formatDuration = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, "0")}`;

const formatBytes = (bytes: number) =>
  bytes >= 1_000_000 ? `${(bytes / 1_000_000).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1000))} KB`;

/**
 * The attachment shown at its real proportions (letterboxed, never cropped),
 * with its dimensions or duration underneath.
 */
function MediaPreview({
  src,
  contentType,
  alt,
  size,
  className,
  children,
}: {
  src: string;
  contentType?: string | null;
  alt: string;
  /** File size in bytes, when known */
  size?: number | null;
  className?: string;
  /** Extra controls shown on the right of the caption row */
  children?: React.ReactNode;
}) {
  const [meta, setMeta] = useState<{ width: number; height: number; duration?: number } | null>(null);
  const video = isVideoType(contentType);
  const mediaClasses = "block max-h-[360px] w-auto max-w-full object-contain";

  const details = [
    video ? "MP4 video" : (contentType?.split("/")[1]?.toUpperCase() ?? "Image"),
    meta && `${meta.width} × ${meta.height}`,
    meta?.duration !== undefined && Number.isFinite(meta.duration) && formatDuration(meta.duration),
    size && formatBytes(size),
  ].filter(Boolean);

  return (
    <figure className={cn("space-y-1.5", className)}>
      <div className="flex min-h-40 w-full items-center justify-center overflow-hidden rounded-lg border border-border bg-muted/60">
        {video ? (
          <video
            src={src}
            aria-label={alt || undefined}
            className={mediaClasses}
            controls
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={(e) =>
              setMeta({
                width: e.currentTarget.videoWidth,
                height: e.currentTarget.videoHeight,
                duration: e.currentTarget.duration,
              })
            }
          />
        ) : (
          <img
            src={src}
            alt={alt}
            className={mediaClasses}
            onLoad={(e) => setMeta({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })}
          />
        )}
      </div>
      <figcaption className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] text-muted-foreground">
        <span className="tabular-nums">{details.join(" · ")}</span>
        <a href={src} target="_blank" rel="noreferrer" className="underline-offset-2 hover:text-foreground hover:underline">
          Open full size
        </a>
        {children && <span className="ml-auto flex items-center gap-1">{children}</span>}
      </figcaption>
    </figure>
  );
}

function LengthCounter({ platform, text }: { platform: SocialPlatform; text: string }) {
  const { length, max } = postLength(platform, text);
  const over = length > max;
  return (
    <span className={cn("font-mono text-[10px] tabular-nums", over ? "text-destructive" : "text-muted-foreground")}>
      {PLATFORM_LABEL[platform]} {length}/{max}
    </span>
  );
}

// ─── Accounts ─────────────────────────────────────────────────────────────────

type ConnectTarget = SocialConnectTarget;

function connectTargetFor(account: SocialAccount): ConnectTarget {
  return account.accountType === "organization" ? "linkedin_page" : account.platform;
}

function ConnectButton({
  target,
  label,
  disabled,
  connecting,
  onConnect,
}: {
  target: ConnectTarget;
  label: string;
  disabled: boolean;
  connecting: ConnectTarget | "callback" | null;
  onConnect: (target: ConnectTarget) => void;
}) {
  return (
    <Button size="sm" variant="outline" disabled={disabled || connecting !== null} onClick={() => onConnect(target)}>
      {connecting === target ? (
        <Icon name="refresh" size={12} className="mr-1.5 animate-spin" />
      ) : (
        <Icon name="add-01" size={12} className="mr-1.5" />
      )}
      {label}
    </Button>
  );
}

function AccountsSection({
  config,
  accounts,
  isLoading,
  connecting,
  onConnect,
  onDisconnect,
}: {
  config: SocialConfig | null;
  accounts: SocialAccount[];
  isLoading: boolean;
  connecting: ConnectTarget | "callback" | null;
  onConnect: (target: ConnectTarget) => void;
  onDisconnect: (account: SocialAccount) => void;
}) {
  // Platforms switched off with SOCIAL_PLATFORMS on the worker are hidden. An
  // older worker may not report a platform at all, so every lookup is optional.
  const platforms = ALL_PLATFORMS.filter((p) => config?.[p]?.enabled ?? p === "linkedin");
  return (
    <section className={cn("grid gap-3", platforms.length > 1 && "md:grid-cols-2", platforms.length > 2 && "xl:grid-cols-3")}>
      {platforms.map((platform) => {
        const platformAccounts = accounts.filter((a) => a.platform === platform);
        const configured = config?.[platform]?.configured ?? false;
        return (
          <div key={platform} className="rounded-lg border border-border bg-card p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Icon name={PLATFORM_ICON[platform]} size={16} />
                <p className="font-mono text-[11px] font-semibold uppercase tracking-widest">{PLATFORM_LABEL[platform]}</p>
              </div>
              <div className="flex gap-1.5">
                {platform === "linkedin" ? (
                  <>
                    <ConnectButton target="linkedin" label="Profile" disabled={!configured} connecting={connecting} onConnect={onConnect} />
                    <ConnectButton
                      target="linkedin_page"
                      label="Company page"
                      disabled={!config?.linkedinPages?.configured}
                      connecting={connecting}
                      onConnect={onConnect}
                    />
                  </>
                ) : (
                  <ConnectButton target={platform} label="Connect" disabled={!configured} connecting={connecting} onConnect={onConnect} />
                )}
              </div>
            </div>

            {isLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : !configured ? (
              <p className="font-sans text-xs text-muted-foreground">
                Not set up. Add {platform === "linkedin" ? "LINKEDIN_CLIENT_ID and LINKEDIN_CLIENT_SECRET" : "X_CLIENT_ID and X_CLIENT_SECRET"} as
                worker secrets, then connect an account here.
              </p>
            ) : platformAccounts.length === 0 ? (
              <p className="font-sans text-xs text-muted-foreground">
                {platform === "linkedin"
                  ? "Connect your profile to post as yourself, or a company page to post as Peersheep."
                  : platform === "bluesky"
                    ? "Connect with your handle and an app password."
                    : "No X account connected yet."}
              </p>
            ) : (
              <ul className="space-y-2">
                {platformAccounts.map((account) => {
                  const isPage = account.accountType === "organization";
                  const needsReconnect = account.status === "reconnect";
                  const expiresSoon =
                    account.tokenExpiresAt &&
                    new Date(account.tokenExpiresAt).getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000;
                  return (
                    <li key={account.id} className="flex items-center gap-2.5">
                      <AccountAvatar account={account} />
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-1.5 truncate font-sans text-sm font-medium">
                          {account.displayName}
                          {isPage && <Badge variant="secondary" className="px-1.5 py-0 text-[9px]">Page</Badge>}
                        </p>
                        <p className={cn("truncate font-sans text-[11px]", needsReconnect ? "text-destructive" : expiresSoon ? "text-amber-500" : "text-muted-foreground")}>
                          {needsReconnect
                            ? account.lastError ?? "Access expired. Reconnect to keep posting."
                            : account.tokenExpiresAt
                              ? `Access expires ${formatDistanceToNow(new Date(account.tokenExpiresAt), { addSuffix: true })}`
                              : isPage
                                ? account.username
                                  ? `linkedin.com/company/${account.username}`
                                  : "Company page"
                                : account.username
                                  ? `@${account.username}`
                                  : "Connected"}
                        </p>
                      </div>
                      {(needsReconnect || expiresSoon) && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="px-2 text-[10px]"
                          onClick={() => onConnect(connectTargetFor(account))}
                          disabled={connecting !== null}
                        >
                          Reconnect
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="px-2 text-[10px] text-muted-foreground hover:text-destructive"
                        onClick={() => onDisconnect(account)}
                      >
                        Disconnect
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </section>
  );
}

// ─── Post list ────────────────────────────────────────────────────────────────

function PostCard({
  post,
  busy,
  onEdit,
  onPublishNow,
  onRetry,
  onUnschedule,
  onDelete,
}: {
  post: SocialPost;
  busy: boolean;
  onEdit: () => void;
  onPublishNow: () => void;
  onRetry: () => void;
  onUnschedule: () => void;
  onDelete: () => void;
}) {
  const badge = STATUS_BADGE[post.status];
  const when =
    post.status === "published" || post.status === "partial"
      ? `Published ${formatWhen(post.publishedAt)}`
      : post.status === "scheduled" || post.status === "publishing"
        ? `Scheduled for ${formatWhen(post.scheduledAt)}`
        : post.status === "failed"
          ? `Failed ${post.updatedAt ? formatDistanceToNow(new Date(post.updatedAt), { addSuffix: true }) : ""}`
          : `Draft, edited ${formatDistanceToNow(new Date(post.updatedAt), { addSuffix: true })}`;

  const canEdit = ["draft", "scheduled", "failed"].includes(post.status);

  return (
    <article className="rounded-lg border border-border bg-card p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={badge.variant} className="text-[10px]">
          {post.status === "publishing" && <Icon name="refresh" size={10} className="mr-1 animate-spin" />}
          {badge.label}
        </Badge>
        <span className="font-mono text-[11px] text-muted-foreground">{when}</span>

        <div className="ml-auto flex flex-wrap items-center gap-1">
          {canEdit && (
            <Button size="sm" variant="ghost" className="px-2 text-[10px]" onClick={onEdit} disabled={busy}>
              <Icon name="edit-02" size={12} className="mr-1" />Edit
            </Button>
          )}
          {(post.status === "draft" || post.status === "scheduled") && (
            <Button size="sm" variant="outline" className="px-2 text-[10px]" onClick={onPublishNow} disabled={busy}>
              {busy ? <Icon name="refresh" size={12} className="mr-1 animate-spin" /> : <Icon name="sent" size={12} className="mr-1" />}
              Publish now
            </Button>
          )}
          {post.status === "scheduled" && (
            <Button size="sm" variant="ghost" className="px-2 text-[10px]" onClick={onUnschedule} disabled={busy}>
              Move to drafts
            </Button>
          )}
          {(post.status === "failed" || post.status === "partial") && (
            <Button size="sm" variant="outline" className="px-2 text-[10px]" onClick={onRetry} disabled={busy}>
              {busy ? <Icon name="refresh" size={12} className="mr-1 animate-spin" /> : <Icon name="arrow-reload-horizontal" size={12} className="mr-1" />}
              Retry failed
            </Button>
          )}
          {post.status !== "publishing" && (
            <Button
              variant="ghost"
               size="icon-sm" className="text-muted-foreground hover:text-destructive"
              onClick={onDelete}
              disabled={busy}
              aria-label="Delete post"
            >
              <Icon name="delete-02" size={13} />
            </Button>
          )}
        </div>
      </div>

      <p className="whitespace-pre-wrap font-sans text-sm leading-relaxed line-clamp-6">{post.body}</p>
      {post.imageKey && (
        <AuthedMedia imageKey={post.imageKey} contentType={post.imageType} alt={post.imageAlt ?? "Post media"} />
      )}

      <ul className="space-y-1.5 border-t border-border pt-3">
        {post.targets.map((target) => (
          <li key={target.id} className="flex flex-wrap items-center gap-2 font-sans text-xs">
            <Icon name={PLATFORM_ICON[target.platform]} size={13} className="text-muted-foreground" />
            <span className="font-medium">{target.accountName}</span>
            {target.accountType === "organization" && <span className="text-muted-foreground">(Page)</span>}
            {target.bodyOverride && <span className="text-muted-foreground">(custom text)</span>}
            <span
              className={cn(
                "font-mono text-[10px] uppercase tracking-widest",
                target.status === "published" ? "text-emerald-500" : target.status === "failed" ? "text-destructive" : "text-muted-foreground",
              )}
            >
              {target.status}
            </span>
            {target.externalUrl && (
              <a
                href={target.externalUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                View post<Icon name="link-square-02" size={11} />
              </a>
            )}
            {target.error && <span className="basis-full pl-5 text-[11px] text-destructive">{target.error}</span>}
          </li>
        ))}
        {post.targets.length === 0 && (
          <li className="font-sans text-xs text-muted-foreground">No accounts selected (the account may have been disconnected).</li>
        )}
      </ul>
    </article>
  );
}

// ─── Composer ─────────────────────────────────────────────────────────────────

interface ComposerProps {
  open: boolean;
  post: SocialPost | null;
  accounts: SocialAccount[];
  onClose: () => void;
  onSaved: (post: SocialPost) => void;
}

function Composer({ open, post, accounts, onClose, onSaved }: ComposerProps) {
  const [body, setBody] = useState("");
  const [customX, setCustomX] = useState(false);
  const [xBody, setXBody] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [scheduleAt, setScheduleAt] = useState(defaultScheduleTime());
  const [image, setImage] = useState<{ key: string; type: string; previewUrl: string | null; size?: number } | null>(null);
  const [imageAlt, setImageAlt] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [topic, setTopic] = useState("");
  const [isDrafting, setIsDrafting] = useState(false);
  const [saving, setSaving] = useState<"draft" | "schedule" | "publish" | null>(null);
  const [attempted, setAttempted] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  // Read accounts through a ref so reloading them doesn't reset a form in progress
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;

  // Reset the form whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    const xOverride = post?.targets.find((t) => t.platform !== "linkedin" && t.bodyOverride)?.bodyOverride ?? "";
    setBody(post?.body ?? "");
    setCustomX(Boolean(xOverride));
    setXBody(xOverride);
    setSelected(
      post
        ? post.targets.map((t) => t.accountId)
        : accountsRef.current.filter((a) => a.status === "active").map((a) => a.id),
    );
    setScheduleAt(post?.scheduledAt ? toLocalInputValue(new Date(post.scheduledAt)) : defaultScheduleTime());
    setImage(post?.imageKey ? { key: post.imageKey, type: post.imageType ?? "", previewUrl: null } : null);
    setImageAlt(post?.imageAlt ?? "");
    setTopic("");
    setAttempted(false);
  }, [open, post]);

  const selectedAccounts = accounts.filter((a) => selected.includes(a.id));
  const hasLinkedIn = selectedAccounts.some((a) => a.platform === "linkedin");
  // X and Bluesky share the optional short version
  const shortPlatforms = useMemo(
    () =>
      (["x", "bluesky"] as const).filter((p) =>
        accounts.some((a) => a.platform === p && selected.includes(a.id)),
      ),
    [accounts, selected],
  );
  const hasX = shortPlatforms.length > 0;
  const shortLabel = shortPlatforms.map((p) => PLATFORM_LABEL[p]).join(" and ") || "X";
  const xText = customX && xBody.trim() ? xBody : body;

  const problems = useMemo(() => {
    const list: string[] = [];
    if (selected.length === 0) list.push("Pick at least one account.");
    if (!body.trim() && !(hasX && !hasLinkedIn && xText.trim())) list.push("Write the post text.");
    if (hasLinkedIn && postLength("linkedin", body).length > postLength("linkedin", body).max) {
      list.push("The LinkedIn text is too long.");
    }
    for (const platform of shortPlatforms) {
      if (postLength(platform, xText).length > postLength(platform, xText).max) {
        list.push(
          customX
            ? `The short version is too long for ${PLATFORM_LABEL[platform]}.`
            : `Too long for ${PLATFORM_LABEL[platform]}. Turn on a separate short version.`,
        );
        break;
      }
    }
    return list;
  }, [selected, body, hasLinkedIn, hasX, shortPlatforms, xText, customX]);

  const toggleAccount = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleDraftWithAI = async () => {
    if (topic.trim().length < 3) {
      toast.error("Describe the topic in a few words first.");
      return;
    }
    setIsDrafting(true);
    const res = await adminSocial.draft(topic.trim(), body.trim() || undefined);
    setIsDrafting(false);
    if ("error" in res && res.error) {
      toast.error(res.error.message);
      return;
    }
    if (res.data) {
      if (res.data.linkedin) setBody(res.data.linkedin);
      if (res.data.x) {
        setXBody(res.data.x);
        setCustomX(true);
      }
      toast.success("Draft ready. Edit it before posting.");
    }
  };

  const handleImage = async (file: File | undefined) => {
    if (!file) return;
    const platforms = accounts.filter((a) => selected.includes(a.id)).map((a) => a.platform);
    const problem = mediaProblem(file, platforms);
    if (problem) {
      toast.error(`${file.name} ${problem}.`);
      return;
    }
    setIsUploading(true);
    const res = await adminSocial.uploadImage(file);
    setIsUploading(false);
    if (res.error || !res.data) {
      toast.error(res.error?.message ?? "Upload failed");
      return;
    }
    setImage({ key: res.data.key, type: res.data.contentType, previewUrl: URL.createObjectURL(file), size: file.size });
  };

  const buildInput = (status: "draft" | "scheduled", scheduledAt: string | null): SocialPostInput => ({
    body: body.trim() ? body : xText,
    status,
    scheduledAt,
    targets: selected.map((accountId) => {
      const account = accounts.find((a) => a.id === accountId);
      return {
        accountId,
        bodyOverride: account && account.platform !== "linkedin" && customX && xBody.trim() ? xBody : null,
      };
    }),
    imageKey: image?.key ?? null,
    imageType: image?.type || null,
    imageAlt: image ? imageAlt.trim() || null : null,
  });

  const save = async (mode: "draft" | "schedule" | "publish") => {
    setAttempted(true);
    if (problems.length > 0) {
      toast.error(problems[0]);
      return;
    }
    let scheduledAt: string | null = null;
    if (mode === "schedule") {
      const date = new Date(scheduleAt);
      if (Number.isNaN(date.getTime())) {
        toast.error("Pick a date and time.");
        return;
      }
      scheduledAt = date.toISOString();
    }

    setSaving(mode);
    const input = buildInput(mode === "schedule" ? "scheduled" : "draft", scheduledAt);
    const saved = post ? await adminSocial.updatePost(post.id, input) : await adminSocial.createPost(input);
    if ("error" in saved && saved.error) {
      setSaving(null);
      toast.error(saved.error.message);
      return;
    }
    let result = saved.data!;

    if (mode === "publish") {
      const published = await adminSocial.publishNow(result.id);
      if ("error" in published && published.error) {
        setSaving(null);
        toast.error(published.error.message);
        onSaved(result);
        return;
      }
      result = published.data!;
      const failed = result.targets.filter((t) => t.status === "failed");
      if (failed.length === 0) toast.success("Posted.");
      else toast.error(`${failed.length} of ${result.targets.length} failed: ${failed[0].error ?? "unknown error"}`);
    } else {
      toast.success(mode === "schedule" ? `Scheduled for ${formatWhen(result.scheduledAt)}` : "Draft saved");
    }

    setSaving(null);
    onSaved(result);
  };

  const schedulePassed = new Date(scheduleAt).getTime() < Date.now();

  return (
    <Dialog open={open} onOpenChange={(value) => !value && !saving && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{post ? "Edit post" : "New post"}</DialogTitle>
          <DialogDescription>Write once, then post now or on a schedule.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Accounts */}
          <div className="space-y-2">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Post to</Label>
            {accounts.length === 0 ? (
              <p className="font-sans text-xs text-muted-foreground">Connect an account first.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {accounts.map((account) => {
                  const isSelected = selected.includes(account.id);
                  const disabled = account.status === "reconnect" && !isSelected;
                  return (
                    <button
                      key={account.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => toggleAccount(account.id)}
                      title={disabled ? "Reconnect this account first" : undefined}
                      className={cn(
                        "flex items-center gap-2 rounded-full border px-2.5 py-1 font-sans text-xs transition-colors disabled:opacity-40",
                        isSelected ? "border-foreground bg-foreground text-background" : "border-border hover:bg-accent",
                      )}
                    >
                      <Icon name={PLATFORM_ICON[account.platform]} size={12} />
                      {account.displayName}
                      {account.accountType === "organization" ? (
                        <span className="opacity-60">Page</span>
                      ) : (
                        account.username && <span className="opacity-60">@{account.username}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* AI draft */}
          <div className="space-y-2 rounded-lg border border-dashed border-border p-3">
            <Label htmlFor="social-topic" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Draft with AI (optional)
            </Label>
            <div className="flex gap-2">
              <Input
                id="social-topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. why careers pages reveal a competitor's roadmap"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void handleDraftWithAI();
                  }
                }}
              />
              <Button type="button" variant="outline" onClick={() => void handleDraftWithAI()} disabled={isDrafting}>
                {isDrafting ? <Icon name="refresh" size={12} className="mr-1.5 animate-spin" /> : <Icon name="ai-magic" size={12} className="mr-1.5" />}
                Draft
              </Button>
            </div>
            <p className="font-sans text-[11px] text-muted-foreground">
              Uses the text below as notes if there is any. Always read the draft before posting.
            </p>
          </div>

          {/* Main text */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="social-body" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {hasX && !customX && hasLinkedIn ? `Post text (LinkedIn and ${shortLabel})` : "Post text"}
              </Label>
              <div className="flex gap-3">
                {(hasLinkedIn || !hasX) && <LengthCounter platform="linkedin" text={body} />}
                {!customX && shortPlatforms.map((p) => <LengthCounter key={p} platform={p} text={body} />)}
              </div>
            </div>
            <Textarea
              id="social-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={9}
              placeholder="What do you want to say?"
              className="font-sans text-sm leading-relaxed"
            />
          </div>

          {/* X version */}
          {hasX && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Switch id="social-custom-x" checked={customX} onCheckedChange={setCustomX} />
                <Label htmlFor="social-custom-x" className="font-sans text-xs">
                  Separate, shorter version for {shortLabel}
                </Label>
                {customX && (
                  <span className="ml-auto flex gap-3">
                    {shortPlatforms.map((p) => <LengthCounter key={p} platform={p} text={xBody} />)}
                  </span>
                )}
              </div>
              {customX && (
                <Textarea
                  value={xBody}
                  onChange={(e) => setXBody(e.target.value)}
                  rows={4}
                  placeholder={shortPlatforms.includes("x") ? "Up to 280 characters. Links count as 23 on X." : "Up to 300 characters."}
                  className="font-sans text-sm"
                />
              )}
            </div>
          )}

          {/* Image */}
          <div className="space-y-2">
            <Label className="block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Image or video (optional)
            </Label>
            <input
              ref={fileInput}
              type="file"
              accept={MEDIA_ACCEPT}
              className="hidden"
              onChange={(e) => {
                void handleImage(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            {image ? (
              <div className="space-y-2">
                {(() => {
                  const controls = (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="px-2 text-[10px]"
                        onClick={() => fileInput.current?.click()}
                        disabled={isUploading}
                      >
                        {isUploading ? <Icon name="refresh" size={11} className="mr-1 animate-spin" /> : <Icon name="exchange-01" size={11} className="mr-1" />}
                        Replace
                      </Button>
                      <Button type="button" size="sm" variant="ghost" className="px-2 text-[10px]" onClick={() => setImage(null)}>
                        <Icon name="delete-02" size={11} className="mr-1" />
                        Remove
                      </Button>
                    </>
                  );
                  return image.previewUrl ? (
                    <MediaPreview src={image.previewUrl} contentType={image.type} alt={imageAlt} size={image.size}>
                      {controls}
                    </MediaPreview>
                  ) : (
                    <AuthedMedia imageKey={image.key} contentType={image.type} alt={imageAlt}>
                      {controls}
                    </AuthedMedia>
                  );
                })()}
                <Input
                  value={imageAlt}
                  onChange={(e) => setImageAlt(e.target.value)}
                  placeholder={isVideoType(image.type) ? "Alt text: describe the video (used on Bluesky)" : "Alt text: describe the image"}
                />
              </div>
            ) : (
              <div>
                <Button type="button" size="sm" variant="outline" onClick={() => fileInput.current?.click()} disabled={isUploading}>
                  {isUploading ? <Icon name="refresh" size={12} className="mr-1.5 animate-spin" /> : <Icon name="image-add-01" size={12} className="mr-1.5" />}
                  Add image or video
                </Button>
                <p className="mt-1.5 font-sans text-[11px] text-muted-foreground">
                  Images up to 5 MB (1 MB for Bluesky). MP4 videos up to 50 MB; Bluesky allows 3 minutes, X 2 minutes 20 seconds.
                </p>
              </div>
            )}
          </div>

          {/* Schedule */}
          <div className="space-y-1.5">
            <Label htmlFor="social-when" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Schedule for ({TIME_ZONE})
            </Label>
            <Input
              id="social-when"
              type="datetime-local"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
              className="w-auto"
            />
            <p className="font-sans text-[11px] text-muted-foreground">
              {schedulePassed
                ? "That time has passed, so a scheduled post will go out within 5 minutes."
                : "Scheduled posts go out within 5 minutes of this time."}
            </p>
          </div>

          {problems.length > 0 && (attempted || body.trim() || xBody.trim()) && (
            <p className="font-sans text-xs text-destructive">{problems[0]}</p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => void save("draft")} disabled={saving !== null}>
            {saving === "draft" && <Icon name="refresh" size={12} className="mr-1.5 animate-spin" />}Save draft
          </Button>
          <Button variant="outline" onClick={() => void save("publish")} disabled={saving !== null || problems.length > 0}>
            {saving === "publish" && <Icon name="refresh" size={12} className="mr-1.5 animate-spin" />}Publish now
          </Button>
          <Button onClick={() => void save("schedule")} disabled={saving !== null || problems.length > 0}>
            {saving === "schedule" ? <Icon name="refresh" size={12} className="mr-1.5 animate-spin" /> : <Icon name="calendar-03" size={12} className="mr-1.5" />}
            Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Bluesky connect ──────────────────────────────────────────────────────────

function BlueskyConnectDialog({
  open,
  onClose,
  onConnected,
}: {
  open: boolean;
  onClose: () => void;
  onConnected: (accounts: SocialAccount[]) => void;
}) {
  const [handle, setHandle] = useState("");
  const [appPassword, setAppPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setHandle("");
    setAppPassword("");
  }, [open]);

  const submit = async () => {
    if (!handle.trim() || !appPassword.trim()) {
      toast.error("Enter the handle and an app password.");
      return;
    }
    setSaving(true);
    const res = await adminSocial.connectBluesky(handle.trim(), appPassword.trim());
    setSaving(false);
    if ("error" in res && res.error) {
      toast.error(res.error.message);
      return;
    }
    if (res.data) onConnected(res.data.accounts);
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && !saving && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Connect Bluesky</DialogTitle>
          <DialogDescription>
            Use an app password, not your main password. Create one in Bluesky under Settings <Icon name="arrow-right-01" size={11} className="mx-0.5 inline-block align-[-2px]" /> Privacy and
            security <Icon name="arrow-right-01" size={11} className="mx-0.5 inline-block align-[-2px]" /> App passwords.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="bsky-handle" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Handle
            </Label>
            <Input
              id="bsky-handle"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="peersheep.bsky.social"
              autoComplete="username"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bsky-password" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              App password
            </Label>
            <Input
              id="bsky-password"
              type="password"
              value={appPassword}
              onChange={(e) => setAppPassword(e.target.value)}
              placeholder="xxxx-xxxx-xxxx-xxxx"
              autoComplete="off"
            />
            <p className="font-sans text-[11px] text-muted-foreground">
              Stored encrypted. Revoking the app password in Bluesky disconnects Peersheep.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving && <Icon name="refresh" size={12} className="mr-1.5 animate-spin" />}Connect
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── CSV import ───────────────────────────────────────────────────────────────

const ACTION_BADGE: Record<BulkRow["action"], { label: string; variant: "secondary" | "outline" | "warning" }> = {
  now: { label: "Now", variant: "warning" },
  schedule: { label: "Schedule", variant: "outline" },
  draft: { label: "Draft", variant: "secondary" },
};

function downloadTemplate() {
  const url = URL.createObjectURL(new Blob([CSV_TEMPLATE], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = "peersheep-social-posts.csv";
  link.click();
  URL.revokeObjectURL(url);
}

function ImportDialog({
  open,
  accounts,
  onClose,
  onImported,
}: {
  open: boolean;
  accounts: SocialAccount[];
  onClose: () => void;
  onImported: () => void;
}) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [csvText, setCsvText] = useState<string | null>(null);
  const [images, setImages] = useState<File[]>([]);
  const [staggerMinutes, setStaggerMinutes] = useState(0);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const imageInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setFileName(null);
    setCsvText(null);
    setImages([]);
    setStaggerMinutes(0);
    setProgress(null);
  }, [open]);

  const readFile = async (file: File | undefined) => {
    if (!file) return;
    setFileName(file.name);
    setCsvText(await file.text());
  };

  // Re-checked whenever images are added, so "not chosen yet" rows clear up
  const { rows, error: fileError } = useMemo(
    () => (csvText === null ? { rows: [] as BulkRow[], error: null } : buildRows(csvText, accounts, images)),
    [csvText, accounts, images],
  );

  const addImages = (files: FileList | null) => {
    if (!files?.length) return;
    // A file with the same name replaces the earlier pick
    const picked = [...files];
    setImages((current) => [...current.filter((f) => !picked.some((p) => p.name === f.name)), ...picked]);
  };

  const valid = rows.filter((r) => r.problems.length === 0);
  const invalid = rows.length - valid.length;
  const nowCount = valid.filter((r) => r.action === "now").length;
  const withImages = valid.filter((r) => r.image);
  const usesImages = rows.some((r) => r.image);

  /** Upload each row's image (its own copy, since deleting a post deletes its image). */
  const uploadImages = async (): Promise<Map<number, UploadedImage> | null> => {
    const uploaded = new Map<number, UploadedImage>();
    for (const [i, row] of withImages.entries()) {
      setProgress(`Uploading media ${i + 1}/${withImages.length}`);
      const res = row.imageFile
        ? await adminSocial.uploadImage(row.imageFile)
        : await adminSocial.imageFromUrl(row.image);
      if (res.error || !res.data) {
        toast.error(`Line ${row.line}: ${res.error?.message ?? "Image upload failed"}`);
        return null;
      }
      // Linked files are only sized once the worker has downloaded them
      const { size, contentType } = res.data;
      const blueskyTooBig = !isVideoType(contentType) && size > BLUESKY_MAX_IMAGE_BYTES;
      if (isImageLink(row.image) && blueskyTooBig && row.accounts.some((a) => a.platform === "bluesky")) {
        toast.error(`Line ${row.line}: the linked image is ${formatMb(size)}; Bluesky allows 1 MB`);
        return null;
      }
      uploaded.set(row.line, { key: res.data.key, contentType: res.data.contentType });
    }
    return uploaded;
  };

  const submit = async () => {
    if (valid.length === 0) return;
    setImporting(true);
    const uploaded = await uploadImages();
    if (!uploaded) {
      setImporting(false);
      setProgress(null);
      return;
    }
    setProgress("Saving posts");
    const res = await adminSocial.bulkCreatePosts(toBulkInput(valid, staggerMinutes, uploaded));
    setImporting(false);
    setProgress(null);
    if ("error" in res && res.error) {
      // The worker numbers the rows it was sent; point back at the file's line
      const message = res.error.message.replace(/^Row (\d+):/, (_, n: string) => `Line ${valid[Number(n) - 1]?.line ?? n}:`);
      toast.error(message);
      return;
    }
    if (!res.data) return;
    const parts = [
      res.data.publishingNow && `${res.data.publishingNow} publishing now`,
      res.data.scheduled && `${res.data.scheduled} scheduled`,
      res.data.drafts && `${res.data.drafts} saved as drafts`,
    ].filter(Boolean);
    toast.success(`Imported ${res.data.created} post${res.data.created === 1 ? "" : "s"}: ${parts.join(", ")}.`);
    onImported();
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && !importing && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import posts from CSV</DialogTitle>
          <DialogDescription>
            Up to {MAX_BULK_ROWS} posts. Tag each row with an action: <strong>now</strong> publishes right away,{" "}
            <strong>schedule</strong> uses its scheduled_at time ({TIME_ZONE}), <strong>draft</strong> saves it for later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-dashed border-border p-3 font-sans text-xs text-muted-foreground space-y-1.5">
            <p>
              Columns: <code className="font-mono">text</code> (required), <code className="font-mono">action</code>,{" "}
              <code className="font-mono">scheduled_at</code> (YYYY-MM-DD HH:mm), <code className="font-mono">accounts</code>{" "}
              (linkedin, x, bluesky, or a handle; blank means every connected account),{" "}
              <code className="font-mono">short_text</code> (shorter version for X and Bluesky),{" "}
              <code className="font-mono">image</code> (an image or MP4 video: a file name you choose below, or an https link) and{" "}
              <code className="font-mono">image_alt</code>. Hashtags in the text become clickable tags.
            </p>
            <Button type="button" size="sm" variant="ghost" className="px-2 text-[10px]" onClick={downloadTemplate}>
              <Icon name="download-04" size={12} className="mr-1" />Download template
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileInput}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                void readFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <Button type="button" variant="outline" size="sm" onClick={() => fileInput.current?.click()} disabled={importing}>
              <Icon name="csv-01" size={12} className="mr-1.5" />
              {fileName ? "Choose another file" : "Choose CSV file"}
            </Button>
            {fileName && <span className="font-mono text-[11px] text-muted-foreground">{fileName}</span>}
            {usesImages && (
              <>
                <input
                  ref={imageInput}
                  type="file"
                  multiple
                  accept={MEDIA_ACCEPT}
                  className="hidden"
                  onChange={(e) => {
                    addImages(e.target.files);
                    e.target.value = "";
                  }}
                />
                <Button type="button" variant="outline" size="sm" onClick={() => imageInput.current?.click()} disabled={importing}>
                  <Icon name="image-add-01" size={12} className="mr-1.5" />
                  Choose images or videos
                </Button>
                {images.length > 0 && (
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {images.length} file{images.length === 1 ? "" : "s"} chosen
                  </span>
                )}
              </>
            )}
          </div>

          {fileError && <p className="font-sans text-xs text-destructive">{fileError}</p>}

          {rows.length > 0 && (
            <>
              <p className="font-sans text-xs">
                <span className="font-medium">{valid.length} ready</span>
                {invalid > 0 && <span className="text-destructive"> · {invalid} with problems (skipped)</span>}
              </p>
              <div className="max-h-80 overflow-y-auto rounded-lg border border-border">
                <table className="w-full font-sans text-xs">
                  <thead className="sticky top-0 bg-muted text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="px-2 py-1.5">Line</th>
                      <th className="px-2 py-1.5">Post</th>
                      <th className="px-2 py-1.5">Action</th>
                      <th className="px-2 py-1.5">Accounts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.line} className={cn("border-t border-border align-top", row.problems.length > 0 && "bg-destructive/5")}>
                        <td className="px-2 py-1.5 font-mono text-muted-foreground">{row.line}</td>
                        <td className="px-2 py-1.5">
                          <p className="line-clamp-2">{row.text || <span className="text-muted-foreground">(empty)</span>}</p>
                          {row.image && (
                            <p className="mt-0.5 flex items-center gap-1 font-mono text-[10px] text-muted-foreground" title={row.imageAlt || undefined}>
                              <Icon name={/\.mp4$/i.test(row.image) ? "video-01" : "image-01"} size={11} />
                              <span className="truncate">{row.image}</span>
                              {!row.imageAlt && <span className="shrink-0">· no alt text</span>}
                            </p>
                          )}
                          {row.problems.map((problem) => (
                            <p key={problem} className="text-[11px] text-destructive">{problem}</p>
                          ))}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          <Badge variant={ACTION_BADGE[row.action].variant} className="text-[10px]">{ACTION_BADGE[row.action].label}</Badge>
                          {row.scheduledAt && row.action !== "now" && (
                            <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">{formatWhen(row.scheduledAt.toISOString())}</p>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="flex flex-wrap gap-1">
                            {row.accounts.map((account) => (
                              <span key={account.id} className="inline-flex items-center gap-1 text-muted-foreground" title={account.displayName}>
                                <Icon name={PLATFORM_ICON[account.platform]} size={11} />
                                {account.username ?? account.displayName}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {nowCount > 1 && (
                <div className="flex flex-wrap items-center gap-2 font-sans text-xs">
                  <Label htmlFor="stagger" className="font-sans text-xs">
                    Space out the {nowCount} "now" posts every
                  </Label>
                  <Input inputSize="sm"
                    id="stagger"
                    type="number"
                    min={0}
                    max={1440}
                    value={staggerMinutes}
                    onChange={(e) => setStaggerMinutes(Math.max(0, Math.min(1440, Number(e.target.value) || 0)))}
                    className="w-20"
                  />
                  <span className="text-muted-foreground">
                    minutes {staggerMinutes === 0 ? "(0 posts them all at once)" : "(the first goes out now)"}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2 sm:items-center">
          {progress && <span className="font-mono text-[11px] text-muted-foreground sm:mr-auto">{progress}</span>}
          <Button variant="ghost" onClick={onClose} disabled={importing}>Cancel</Button>
          <Button onClick={() => void submit()} disabled={importing || valid.length === 0}>
            {importing ? <Icon name="refresh" size={12} className="mr-1.5 animate-spin" /> : <Icon name="file-import" size={12} className="mr-1.5" />}
            Import {valid.length > 0 ? `${valid.length} post${valid.length === 1 ? "" : "s"}` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

interface LinkedInAutomationSectionProps {
  config: SocialAutomationConfig | null;
  accounts: SocialAccount[];
  isLoading: boolean;
  onConfigChange: (config: SocialAutomationConfig) => void;
  onPostPublished: () => void;
}

function LinkedInAutomationSection({ config, accounts, isLoading, onConfigChange, onPostPublished }: LinkedInAutomationSectionProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [automationPosts, setAutomationPosts] = useState<SocialPost[]>([]);
  const [showPosts, setShowPosts] = useState(false);
  const [recentAlerts, setRecentAlerts] = useState<Alert[]>([]);
  const [selectedAlertId, setSelectedAlertId] = useState("");
  const [preview, setPreview] = useState<{ body: string; workspaceCompanyName: string } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [isPostingPreview, setIsPostingPreview] = useState(false);
  const [selectedPageTypes, setSelectedPageTypes] = useState(
    config?.pageTypeFilter ?? AUTOMATION_PAGE_TYPES.map(({ value }) => value),
  );
  const [selectedLinkedInAccountId, setSelectedLinkedInAccountId] = useState(config?.linkedinAccountId ?? null);

  useEffect(() => {
    setSelectedPageTypes(config?.pageTypeFilter ?? AUTOMATION_PAGE_TYPES.map(({ value }) => value));
    setSelectedLinkedInAccountId(config?.linkedinAccountId ?? null);
  }, [config]);

  const { workspace } = useAuth();

  useEffect(() => {
    let cancelled = false;
    setRecentAlerts([]);
    setSelectedAlertId("");
    setPreview(null);
    setPreviewError(null);
    if (!workspace?.id) return;

    void alertsApi.list({ limit: 50 }).then((res) => {
      if (cancelled || !("data" in res) || !res.data) return;
      setRecentAlerts(res.data.alerts);
      setSelectedAlertId(res.data.alerts[0]?.id ?? "");
    });
    return () => { cancelled = true; };
  }, [workspace?.id]);

  // Load recent auto-generated posts when section is expanded
  useEffect(() => {
    if (!showPosts) return;
    void (async () => {
      const res = await adminSocial.automationPosts(10);
      if ("data" in res && res.data) setAutomationPosts(res.data);
    })();
  }, [showPosts]);

  const linkedinAccounts = accounts.filter((a) => a.platform === "linkedin" && a.status === "active");
  const publishAccountId = linkedinAccounts.some((account) => account.id === selectedLinkedInAccountId)
    ? selectedLinkedInAccountId
    : linkedinAccounts[0]?.id ?? null;
  const localConfig = config || {
    id: "",
    workspaceId: "",
    linkedinAccountId: null,
    enabled: false,
    scheduleMode: "immediate",
    signalScoreThreshold: "high",
    pageTypeFilter: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const handleToggleEnabled = async (enabled: boolean) => {
    const selectedAccountId = linkedinAccounts.some((account) => account.id === selectedLinkedInAccountId)
      ? selectedLinkedInAccountId
      : linkedinAccounts[0]?.id ?? null;

    setIsSaving(true);
    const res = await adminSocial.updateAutomationConfig({
      linkedinAccountId: selectedAccountId,
      pageTypeFilter: selectedPageTypes,
      enabled,
    });
    setIsSaving(false);
    if ("data" in res && res.data) {
      onConfigChange(res.data);
      toast.success(res.data.enabled ? "LinkedIn automation enabled" : "LinkedIn automation disabled");
    } else if ("error" in res && res.error) {
      toast.error(res.error.message);
    }
  };

  const handleUpdateConfig = async (updates: Partial<SocialAutomationConfig>) => {
    setIsSaving(true);
    const res = await adminSocial.updateAutomationConfig(updates);
    setIsSaving(false);
    if ("data" in res && res.data) {
      onConfigChange(res.data);
      toast.success("Configuration saved");
    } else if ("error" in res && res.error) {
      toast.error(res.error.message);
    }
  };

  const generateExample = async () => {
    if (!selectedAlertId) return;
    setIsGeneratingPreview(true);
    setPreview(null);
    setPreviewError(null);
    const res = await adminSocial.previewAutomationPost(selectedAlertId);
    setIsGeneratingPreview(false);
    if ("data" in res && res.data) setPreview(res.data);
    else if ("error" in res && res.error) setPreviewError(res.error.message);
  };

  const publishExample = async () => {
    if (!preview || !publishAccountId) return;
    setIsPostingPreview(true);
    const created = await adminSocial.createPost({
      body: preview.body,
      targets: [{ accountId: publishAccountId }],
      status: "scheduled",
      scheduledAt: new Date(Date.now() + 2 * 60 * 1000).toISOString(),
    });
    if (!("data" in created) || !created.data) {
      setIsPostingPreview(false);
      toast.error("error" in created ? created.error?.message ?? "Couldn't create the post" : "Couldn't create the post");
      return;
    }

    const result = await adminSocial.publishNow(created.data.id);
    setIsPostingPreview(false);
    if (!("data" in result) || !result.data) {
      toast.error("error" in result ? `Post was saved, but couldn't be published: ${result.error?.message ?? "Unknown error"}` : "Post was saved, but couldn't be published");
      onPostPublished();
      return;
    }

    const failedTarget = result.data.targets.find((target) => target.status === "failed");
    if (failedTarget) toast.error(`Post saved, but LinkedIn couldn't publish it: ${failedTarget.error ?? "Unknown error"}`);
    else toast.success(`Posted to ${result.data.targets[0]?.accountName ?? "LinkedIn"}`);
    onPostPublished();
  };

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-sm">LinkedIn Automation</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Auto-post to LinkedIn when high-signal competitor alerts are detected
            </p>
          </div>
          <Switch checked={localConfig.enabled} onCheckedChange={handleToggleEnabled} disabled={isSaving || (!localConfig.enabled && linkedinAccounts.length === 0)} />
        </div>

        <div className="space-y-4 border-t border-border pt-4">
            {/* LinkedIn Account Select */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">LinkedIn Page</Label>
              {linkedinAccounts.length === 0 ? (
                <div className="rounded-md border border-dashed border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                  No active LinkedIn accounts connected. Connect a LinkedIn account in the Publishing tab.
                </div>
              ) : (
                <select
                  className="flex h-8 w-full rounded-md border border-border bg-background px-2 py-1 text-xs"
                  value={selectedLinkedInAccountId || ""}
                  onChange={(e) => {
                    const accountId = e.target.value || null;
                    setSelectedLinkedInAccountId(accountId);
                    void handleUpdateConfig({ linkedinAccountId: accountId });
                  }}
                  disabled={isSaving}
                >
                  <option value="">Select an account</option>
                  {linkedinAccounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.displayName}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Schedule Mode */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Post Schedule</Label>
              <select
                className="flex h-8 w-full rounded-md border border-border bg-background px-2 py-1 text-xs"
                value={localConfig.scheduleMode}
                onChange={(e) => void handleUpdateConfig({ scheduleMode: e.target.value as "immediate" | "next_morning" })}
                disabled={isSaving}
              >
                <option value="immediate">Immediately (with 2-min buffer)</option>
                <option value="next_morning">Next business morning at 9am</option>
              </select>
            </div>

            {/* Signal Score Threshold */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Signal Score Threshold</Label>
              <select
                className="flex h-8 w-full rounded-md border border-border bg-background px-2 py-1 text-xs"
                value={localConfig.signalScoreThreshold}
                onChange={(e) => void handleUpdateConfig({ signalScoreThreshold: e.target.value as "high" | "medium" })}
                disabled={isSaving}
              >
                <option value="high">High signals only</option>
                <option value="medium">Medium or higher</option>
              </select>
            </div>

            <fieldset className="space-y-2">
              <legend className="text-xs font-medium">Watched page types</legend>
              <div className="grid grid-cols-2 gap-2">
                {AUTOMATION_PAGE_TYPES.map(({ value, label }) => {
                  return (
                    <label key={value} className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={selectedPageTypes.includes(value)}
                        disabled={isSaving || isLoading}
                        onChange={(event) => {
                          const next = new Set(selectedPageTypes);
                          if (event.target.checked) next.add(value);
                          else next.delete(value);
                          const pageTypeFilter = [...next];
                          setSelectedPageTypes(pageTypeFilter);
                          void handleUpdateConfig({ pageTypeFilter });
                        }}
                      />
                      {label}
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <div className="space-y-2 border-t border-border pt-3">
              <Label htmlFor="automation-alert">Generate a LinkedIn example from an alert</Label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <select
                  id="automation-alert"
                  className="flex h-8 min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1 text-xs"
                  value={selectedAlertId}
                  onChange={(event) => {
                    setSelectedAlertId(event.target.value);
                    setPreview(null);
                    setPreviewError(null);
                  }}
                  disabled={recentAlerts.length === 0 || isGeneratingPreview}
                >
                  <option value="">{recentAlerts.length ? "Choose an alert" : "No recent alerts"}</option>
                  {recentAlerts.map((alert) => (
                    <option key={alert.id} value={alert.id}>
                      {`${alert.competitor?.name ?? "Competitor"} · ${alert.pageType} · ${formatDistanceToNow(new Date(alert.detectedAt), { addSuffix: true })} · ${alert.summary.slice(0, 80)}`}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void generateExample()}
                  disabled={!selectedAlertId || isGeneratingPreview}
                >
                  {isGeneratingPreview && <Icon name="refresh" size={12} className="mr-1.5 animate-spin" />}
                  Generate example
                </Button>
              </div>
              {previewError && <p role="alert" className="text-xs text-destructive">{previewError}</p>}
              {preview && (
                <div className="space-y-2 rounded border border-border bg-muted/30 p-3">
                  <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    <span>Workspace text: @{preview.workspaceCompanyName}</span>
                    <span>LinkedIn company tag: @Peersheep</span>
                  </div>
                  <p className="whitespace-pre-wrap font-sans text-xs leading-relaxed">{preview.body}</p>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => void publishExample()}
                    disabled={isPostingPreview || !publishAccountId}
                  >
                    {isPostingPreview && <Icon name="refresh" size={12} className="mr-1.5 animate-spin" />}
                    Post to LinkedIn
                  </Button>
                </div>
              )}
            </div>

            {/* Recent Auto-Generated Posts */}
            <div className="border-t border-border pt-3">
              <button
                type="button"
                className="flex items-center gap-2 text-xs font-medium text-foreground hover:text-foreground/80 transition-colors"
                onClick={() => setShowPosts(!showPosts)}
              >
                <Icon name={showPosts ? "chevron-down" : "chevron-right"} size={12} />
                Recent auto-generated posts ({automationPosts.length})
              </button>
              {showPosts && automationPosts.length > 0 && (
                <div className="mt-3 space-y-2 bg-muted/30 rounded p-2">
                  {automationPosts.slice(0, 5).map((post) => (
                    <div key={post.id} className="text-xs border-l-2 border-accent pl-2 py-1">
                      <div className="font-medium text-foreground/80 line-clamp-2">{post.body}</div>
                      <div className="mt-0.5 text-muted-foreground">{formatDistanceToNow(new Date(post.createdAt), { addSuffix: true })}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

        {/* Disabled state message */}
        {!localConfig.enabled && linkedinAccounts.length > 0 && (
          <div className="text-xs text-muted-foreground">LinkedIn automation is currently disabled. Enable it above to start auto-posting competitor alerts.</div>
        )}
      </div>
    </section>
  );
}

function SocialPublishing() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [config, setConfig] = useState<SocialConfig | null>(null);
  const [automationConfig, setAutomationConfig] = useState<SocialAutomationConfig | null>(null);
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>("upcoming");
  const [composer, setComposer] = useState<{ open: boolean; post: SocialPost | null }>({ open: false, post: null });
  const [connecting, setConnecting] = useState<ConnectTarget | "callback" | null>(null);
  const [busyPostId, setBusyPostId] = useState<string | null>(null);
  const [disconnectTarget, setDisconnectTarget] = useState<SocialAccount | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SocialPost | null>(null);
  const [blueskyOpen, setBlueskyOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const callbackHandled = useRef(false);

  const loadAccounts = useCallback(async () => {
    const res = await adminSocial.accounts();
    if ("data" in res && res.data) setAccounts(res.data);
  }, []);

  const loadPosts = useCallback(async () => {
    const res = await adminSocial.posts();
    if ("data" in res && res.data) setPosts(res.data);
  }, []);

  useEffect(() => {
    void Promise.all([
      adminSocial.config().then((res) => "data" in res && res.data && setConfig(res.data)),
      adminSocial.automationConfig().then((res) => "data" in res && res.data && setAutomationConfig(res.data)),
      loadAccounts(),
      loadPosts(),
    ]).finally(() => setIsLoading(false));
  }, [loadAccounts, loadPosts]);

  // OAuth callback: /admin/social/callback?code=...&state=...
  useEffect(() => {
    if (!location.pathname.endsWith("/callback") || callbackHandled.current) return;
    callbackHandled.current = true;

    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const errorCode = searchParams.get("error");
    const error = searchParams.get("error_description") ?? errorCode;
    navigate("/admin/social", { replace: true });

    if (errorCode === "unauthorized_scope_error") {
      // LinkedIn refuses Page scopes unless the app has the Community Management API
      toast.error(
        "LinkedIn hasn't approved this app to post as a company page. That needs the Community Management API on its own LinkedIn app; see docs/SOCIAL_PUBLISHING.md.",
        { duration: 15000 },
      );
      return;
    }
    if (error || !code || !state) {
      toast.error(error ? `Connection cancelled: ${error}` : "Connection failed: no authorization code");
      return;
    }
    setConnecting("callback");
    void adminSocial.completeConnect(code, state).then((res) => {
      setConnecting(null);
      if ("error" in res && res.error) {
        toast.error(res.error.message);
        return;
      }
      if (res.data) {
        const connected = res.data.accounts;
        toast.success(
          connected.length === 1
            ? `Connected ${connected[0].displayName} on ${PLATFORM_LABEL[connected[0].platform]}`
            : `Connected ${connected.length} LinkedIn pages: ${connected.map((a) => a.displayName).join(", ")}`,
        );
        void loadAccounts();
      }
    });
  }, [location.pathname, searchParams, navigate, loadAccounts]);

  // Refresh while something is being published
  const hasPublishing = posts.some((p) => p.status === "publishing");
  useEffect(() => {
    if (!hasPublishing) return;
    const timer = window.setInterval(() => void loadPosts(), 5000);
    return () => window.clearInterval(timer);
  }, [hasPublishing, loadPosts]);

  const handleConnect = async (target: ConnectTarget) => {
    // Bluesky has no OAuth redirect; it connects with an app password
    if (target === "bluesky") {
      setBlueskyOpen(true);
      return;
    }
    setConnecting(target);
    const res = await adminSocial.connect(target, window.location.origin);
    if ("error" in res && res.error) {
      setConnecting(null);
      toast.error(res.error.message);
      return;
    }
    if (res.data) window.location.href = res.data.url;
  };

  const confirmDisconnect = async () => {
    if (!disconnectTarget) return;
    const target = disconnectTarget;
    setDisconnectTarget(null);
    const res = await adminSocial.disconnect(target.id);
    if ("error" in res && res.error) {
      toast.error(res.error.message);
      return;
    }
    toast.success(`${target.displayName} disconnected`);
    void loadAccounts();
    void loadPosts();
  };

  const upsertPost = (post: SocialPost) =>
    setPosts((prev) => [post, ...prev.filter((p) => p.id !== post.id)]);

  const runAction = async (post: SocialPost, action: "publish" | "retry" | "unschedule") => {
    setBusyPostId(post.id);
    const res =
      action === "publish"
        ? await adminSocial.publishNow(post.id)
        : action === "retry"
          ? await adminSocial.retry(post.id)
          : await adminSocial.unschedule(post.id);
    setBusyPostId(null);
    if ("error" in res && res.error) {
      toast.error(res.error.message);
      return;
    }
    if (!res.data) return;
    upsertPost(res.data);
    if (action === "unschedule") {
      toast.success("Moved to drafts");
      return;
    }
    const failed = res.data.targets.filter((t) => t.status === "failed");
    if (failed.length === 0) toast.success("Posted.");
    else toast.error(`${failed.length} of ${res.data.targets.length} failed: ${failed[0].error ?? "unknown error"}`);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    const res = await adminSocial.deletePost(target.id);
    if ("error" in res && res.error) {
      toast.error(res.error.message);
      return;
    }
    setPosts((prev) => prev.filter((p) => p.id !== target.id));
    toast.success("Post deleted");
  };

  const counts = useMemo(() => {
    const result = {} as Record<Filter, number>;
    for (const f of FILTERS) {
      result[f.value] = f.statuses ? posts.filter((p) => f.statuses!.includes(p.status)).length : posts.length;
    }
    return result;
  }, [posts]);

  const visiblePosts = useMemo(() => {
    const statuses = FILTERS.find((f) => f.value === filter)?.statuses;
    const list = statuses ? posts.filter((p) => statuses.includes(p.status)) : posts;
    // Upcoming: soonest first; everything else: newest first
    return filter === "upcoming"
      ? [...list].sort((a, b) => (a.scheduledAt ?? "").localeCompare(b.scheduledAt ?? ""))
      : list;
  }, [posts, filter]);

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Social publishing"
        actions={
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setImportOpen(true)} disabled={accounts.length === 0}>
              <Icon name="file-import" size={12} className="mr-1.5" />Import CSV
            </Button>
            <Button size="sm" onClick={() => setComposer({ open: true, post: null })} disabled={accounts.length === 0}>
              <Icon name="add-01" size={12} className="mr-1.5" />New post
            </Button>
          </div>
        }
      />

      <Tabs defaultValue="publishing" className="flex min-h-0 flex-1 flex-col">
        <TabsList className="mx-auto w-full max-w-5xl justify-start rounded-none border-b border-border bg-transparent px-4 py-2 sm:px-6">
          <TabsTrigger value="publishing">Publishing</TabsTrigger>
          <TabsTrigger value="automation">Alert automation</TabsTrigger>
        </TabsList>

        <TabsContent value="publishing" className="m-0 min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-5xl space-y-6 px-4 py-5 sm:px-6">
          <AccountsSection
            config={config}
            accounts={accounts}
            isLoading={isLoading}
            connecting={connecting}
            onConnect={(platform) => void handleConnect(platform)}
            onDisconnect={setDisconnectTarget}
          />

          <section className="space-y-3">
            <div className="flex flex-wrap gap-1.5">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setFilter(f.value)}
                  className={cn(
                    "rounded-md border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors",
                    filter === f.value ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:bg-accent",
                  )}
                >
                  {f.label}
                  <span className="ml-1.5 opacity-60">{counts[f.value]}</span>
                </button>
              ))}
            </div>

            {isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            ) : visiblePosts.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border px-6 py-12 text-center">
                <p className="font-sans text-sm font-medium">Nothing here yet</p>
                <p className="mt-1 font-sans text-xs text-muted-foreground">
                  {accounts.length === 0
                    ? "Connect an account above to start posting."
                    : "Write a post and schedule it, or publish it straight away."}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {visiblePosts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    busy={busyPostId === post.id}
                    onEdit={() => setComposer({ open: true, post })}
                    onPublishNow={() => void runAction(post, "publish")}
                    onRetry={() => void runAction(post, "retry")}
                    onUnschedule={() => void runAction(post, "unschedule")}
                    onDelete={() => setDeleteTarget(post)}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
        </TabsContent>

        <TabsContent value="automation" className="m-0 min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-5xl px-4 py-5 sm:px-6">
            <LinkedInAutomationSection
              config={automationConfig}
              accounts={accounts}
              isLoading={isLoading}
              onConfigChange={setAutomationConfig}
              onPostPublished={() => { void loadPosts(); }}
            />
          </div>
        </TabsContent>
      </Tabs>

      <Composer
        open={composer.open}
        post={composer.post}
        accounts={accounts}
        onClose={() => setComposer({ open: false, post: null })}
        onSaved={(post) => {
          upsertPost(post);
          setComposer({ open: false, post: null });
          setFilter(post.status === "draft" ? "drafts" : post.status === "scheduled" ? "upcoming" : post.status === "published" ? "published" : "attention");
        }}
      />

      <BlueskyConnectDialog
        open={blueskyOpen}
        onClose={() => setBlueskyOpen(false)}
        onConnected={(connected) => {
          setBlueskyOpen(false);
          toast.success(`Connected ${connected[0]?.displayName ?? "account"} on Bluesky`);
          void loadAccounts();
        }}
      />

      <ImportDialog
        open={importOpen}
        accounts={accounts}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          setImportOpen(false);
          setFilter("upcoming");
          void loadPosts();
        }}
      />

      <Dialog open={!!disconnectTarget} onOpenChange={(open) => !open && setDisconnectTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect {disconnectTarget?.displayName}?</DialogTitle>
            <DialogDescription>
              Peersheep stops posting to this {disconnectTarget ? PLATFORM_LABEL[disconnectTarget.platform] : ""} account and
              removes it from any post that hasn't gone out yet. Published posts stay on{" "}
              {disconnectTarget ? PLATFORM_LABEL[disconnectTarget.platform] : "the platform"}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisconnectTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => void confirmDisconnect()}>Disconnect</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this post?</DialogTitle>
            <DialogDescription>
              {deleteTarget?.status === "published" || deleteTarget?.status === "partial"
                ? "This removes it from Peersheep only. The live post stays up; delete it on the platform if needed."
                : "The post and its schedule are removed. This can't be undone."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => void confirmDelete()}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function SocialPage() {
  const { user, isLoading } = useAuth();

  if (isLoading) return null;
  if (!user?.isAdmin) {
    return (
      <div className="flex h-full flex-col">
        <PageHeader title="Social publishing" />
        <div className="flex flex-1 items-center justify-center p-6">
          <p className="font-sans text-sm text-muted-foreground">This page is for Peersheep admins.</p>
        </div>
      </div>
    );
  }
  return <SocialPublishing />;
}
